import { AUDIO } from '../config/tuning';
import type { AudioEngine } from './AudioEngine';
import { AudioBus } from './AudioEngine';
import { airflowCutoffHz, airflowGain, engineFundamentalHz, stressGain } from './Spatial';

/**
 * The continuous layers: engine, airflow, stall buffet, airframe stress (SPEC §3.11).
 *
 * All four run for the whole flight and are *modulated* rather than started and stopped. Starting
 * an oscillator per frame would click on every restart and cost far more than holding four voices
 * open; `setTargetAtTime` gives a short exponential glide that reads as a machine responding
 * rather than a value being assigned.
 *
 * The buffet is the one with a job beyond atmosphere. SPEC calls it "a free stall warning the
 * player learns without being told", so it is keyed to stall depth and mixed loud enough to be
 * unmistakable before the aircraft departs.
 */

/** Smoothing constant for parameter glides, seconds. Short enough to feel immediate. */
const GLIDE_S = 0.08;

export class EngineVoice {
  private readonly audio: AudioEngine;

  private oscillators: OscillatorNode[] = [];
  private harmonicGains: GainNode[] = [];
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;

  private roughness: OscillatorNode | null = null;
  private roughnessGain: GainNode | null = null;

  private airflowSource: AudioBufferSourceNode | null = null;
  private airflowFilter: BiquadFilterNode | null = null;
  private airflowGainNode: GainNode | null = null;

  private buffetSource: AudioBufferSourceNode | null = null;
  private buffetFilter: BiquadFilterNode | null = null;
  private buffetGainNode: GainNode | null = null;

  private stressOsc: OscillatorNode | null = null;
  private stressGainNode: GainNode | null = null;

  private built = false;

  constructor(audio: AudioEngine) {
    this.audio = audio;
  }

  /** Build the graph. Safe to call repeatedly; does nothing until the context exists. */
  start(): void {
    if (this.built) return;
    const ctx = this.audio.ctx;
    const bus = this.audio.bus(AudioBus.Effects);
    if (ctx === null || bus === null) return;

    // --- Engine: a sawtooth-like stack built from discrete harmonics.
    //
    // Built by hand rather than with a `sawtooth` oscillator because the harmonics need
    // independent gains: a real engine's upper partials come and go with load, and a fixed
    // waveform cannot do that. Amplitude falls as 1/n, which is the sawtooth series.
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = AUDIO.engineFilterHz;
    this.engineFilter.Q.value = 0.7;

    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engineFilter.connect(this.engineGain);
    this.engineGain.connect(bus);

    for (let n = 1; n <= AUDIO.engineHarmonics; n += 1) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = AUDIO.engineBaseHz * n;
      const gain = ctx.createGain();
      gain.gain.value = 1 / (n * n);
      osc.connect(gain);
      gain.connect(this.engineFilter);
      osc.start();
      this.oscillators.push(osc);
      this.harmonicGains.push(gain);
    }

    // Slow amplitude wobble: without it the stack is a pure tone and reads as a synthesiser.
    this.roughness = ctx.createOscillator();
    this.roughness.frequency.value = AUDIO.engineRoughnessHz;
    this.roughnessGain = ctx.createGain();
    this.roughnessGain.gain.value = AUDIO.engineRoughnessDepth;
    this.roughness.connect(this.roughnessGain);
    this.roughnessGain.connect(this.engineGain.gain);
    this.roughness.start();

    // --- Airflow: filtered noise, cutoff and level driven by airspeed.
    this.airflowSource = this.audio.createNoiseSource();
    this.airflowFilter = ctx.createBiquadFilter();
    this.airflowFilter.type = 'lowpass';
    this.airflowFilter.frequency.value = AUDIO.airflowCutoffMinHz;
    this.airflowGainNode = ctx.createGain();
    this.airflowGainNode.gain.value = 0;
    if (this.airflowSource !== null) {
      this.airflowSource.connect(this.airflowFilter);
      this.airflowFilter.connect(this.airflowGainNode);
      this.airflowGainNode.connect(bus);
      this.airflowSource.start();
    }

    // --- Buffet: a narrow resonant band on the same noise, keyed to stall depth.
    this.buffetSource = this.audio.createNoiseSource();
    this.buffetFilter = ctx.createBiquadFilter();
    this.buffetFilter.type = 'bandpass';
    this.buffetFilter.frequency.value = AUDIO.buffetCentreHz;
    this.buffetFilter.Q.value = AUDIO.buffetQ;
    this.buffetGainNode = ctx.createGain();
    this.buffetGainNode.gain.value = 0;
    if (this.buffetSource !== null) {
      this.buffetSource.connect(this.buffetFilter);
      this.buffetFilter.connect(this.buffetGainNode);
      this.buffetGainNode.connect(bus);
      this.buffetSource.start();
    }

    // --- Airframe stress: a low creak that only exists above the threshold g.
    this.stressOsc = ctx.createOscillator();
    this.stressOsc.type = 'triangle';
    this.stressOsc.frequency.value = AUDIO.stressHz;
    this.stressGainNode = ctx.createGain();
    this.stressGainNode.gain.value = 0;
    this.stressOsc.connect(this.stressGainNode);
    this.stressGainNode.connect(bus);
    this.stressOsc.start();

    this.built = true;
  }

  /**
   * Track the aircraft. Called once per rendered frame with the current flight state.
   *
   * `engineRunning` false silences the engine stack but leaves airflow alone — a flameout is not
   * silence, it is the sound of a glider, and that distinction is the whole feedback the player
   * gets that the engine has stopped.
   */
  update(params: {
    throttle: number;
    airspeedMs: number;
    stallDepth: number;
    loadFactor: number;
    engineRunning: boolean;
  }): void {
    const ctx = this.audio.ctx;
    if (!this.built || ctx === null) return;
    const now = ctx.currentTime;

    const fundamental = engineFundamentalHz(params.throttle, params.airspeedMs);
    for (let n = 0; n < this.oscillators.length; n += 1) {
      this.oscillators[n].frequency.setTargetAtTime(fundamental * (n + 1), now, GLIDE_S);
    }

    const engineLevel = params.engineRunning ? AUDIO.engineGain * (0.35 + 0.65 * clamp01(params.throttle)) : 0;
    this.engineGain?.gain.setTargetAtTime(engineLevel, now, GLIDE_S);
    this.engineFilter?.frequency.setTargetAtTime(
      AUDIO.engineFilterHz * (0.6 + 0.8 * clamp01(params.throttle)),
      now,
      GLIDE_S,
    );

    this.airflowFilter?.frequency.setTargetAtTime(airflowCutoffHz(params.airspeedMs), now, GLIDE_S);
    this.airflowGainNode?.gain.setTargetAtTime(
      AUDIO.airflowGain * airflowGain(params.airspeedMs),
      now,
      GLIDE_S,
    );

    this.buffetGainNode?.gain.setTargetAtTime(
      AUDIO.buffetGain * clamp01(params.stallDepth),
      now,
      GLIDE_S,
    );

    this.stressGainNode?.gain.setTargetAtTime(
      AUDIO.stressGain * stressGain(params.loadFactor),
      now,
      GLIDE_S,
    );
  }

  /** Silence everything without tearing the graph down — used by the pause menu. */
  mute(): void {
    const ctx = this.audio.ctx;
    if (!this.built || ctx === null) return;
    const now = ctx.currentTime;
    this.engineGain?.gain.setTargetAtTime(0, now, 0.05);
    this.airflowGainNode?.gain.setTargetAtTime(0, now, 0.05);
    this.buffetGainNode?.gain.setTargetAtTime(0, now, 0.05);
    this.stressGainNode?.gain.setTargetAtTime(0, now, 0.05);
  }

  dispose(): void {
    for (let i = 0; i < this.oscillators.length; i += 1) safeStop(this.oscillators[i]);
    this.oscillators = [];
    this.harmonicGains = [];
    safeStop(this.roughness);
    safeStop(this.airflowSource);
    safeStop(this.buffetSource);
    safeStop(this.stressOsc);
    this.built = false;
  }
}

function safeStop(node: AudioScheduledSourceNode | null): void {
  if (node === null) return;
  try {
    node.stop();
  } catch {
    // Already stopped, or the context is closed. Nothing to do and nothing worth reporting.
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
